import { AfterViewInit, Component, effect, ElementRef, HostListener, inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CanvasService } from '../../services/canvas.service';
import { CanvasObject, Point, RGBA, Size } from '../../models/canvas-object.model';
import { SHAPE_TOOLS, TOOLS } from '../../models/tool.model';

interface DragState {
    isDragging: boolean;
    startPoint: Point;
    currentPoint: Point;
    draggedObjectId?: string;
    isPanning: boolean;
    points?: Point[];
    isResizing?: boolean;
    resizeHandle?: number;
    resizeStart?: Point;
    resizingObjectId?: string;
    originalPosition?: Point;
    originalSize?: Size;
    isSelectionBox?: boolean;
}

@Component({
    selector: 'app-canvas-editor',
    imports: [CommonModule],
    templateUrl: './canvas-editor.component.html',
    styleUrl: './canvas-editor.component.scss'
})
export class CanvasEditorComponent implements AfterViewInit, OnDestroy {
    @ViewChild('canvas', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
    @ViewChild('canvasPreview', { static: false }) canvasPreviewRef?: ElementRef<HTMLCanvasElement>;
    @ViewChild('canvasContainer', { static: false }) canvasContainerRef!: ElementRef<HTMLDivElement>;

    private canvasService = inject(CanvasService);
    private ctx!: CanvasRenderingContext2D;
    private previewCtx?: CanvasRenderingContext2D;
    private isResizing = false;
    private dragState: DragState = {
        isDragging: false,
        startPoint: { x: 0, y: 0 },
        currentPoint: { x: 0, y: 0 },
        isPanning: false
    };
    private imageCache = new Map<string, HTMLImageElement | ImageBitmap>();
    private svgOverlayCache = new Map<string, { hash: number; url: string }>();
    private svgHitTestContainer?: SVGSVGElement;
    private lastCanvasSize: { width: number; height: number; type: string } | null = null;

    readonly objects = this.canvasService.activeObjects;
    readonly allObjects = this.canvasService.objects;
    readonly selectedIds = this.canvasService.selectedIds;
    readonly currentTool = this.canvasService.currentTool;
    readonly zoom = this.canvasService.zoom;
    readonly pan = this.canvasService.pan;
    readonly gridVisible = this.canvasService.gridVisible;
    readonly gridSize = this.canvasService.gridSize;
    readonly canvasSize = this.canvasService.canvasSize;
    readonly canvasSizeType = this.canvasService.canvasSizeTypeValue;
    readonly activeSide = this.canvasService.activeSideValue;

    private lastAutoFitKey = '';
    private lastZoom = 1;

    constructor() {
        // Watch for canvas size changes
        effect(() => {
            const size = this.canvasSize();
            const sizeType = this.canvasSizeType();
            if (
                this.lastCanvasSize &&
                this.lastCanvasSize.width === size.width &&
                this.lastCanvasSize.height === size.height &&
                this.lastCanvasSize.type === sizeType
            ) {
                return;
            }
            this.lastCanvasSize = { width: size.width, height: size.height, type: sizeType };
            if (this.canvasRef) {
                this.resizeCanvas();
                this.canvasService.setZoom(1);
                this.canvasService.setPan({ x: 0, y: 0 });
                this.centerCanvasInView();
                this.render();
            }
        });

        // Watch for view/grid changes that affect rendering
        effect(() => {
            this.gridVisible();
            this.gridSize();
            const zoom = this.zoom();
            this.pan();
            if (this.canvasRef) {
                if (this.canvasContainerRef && zoom !== this.lastZoom) {
                    this.adjustScrollForZoom(this.lastZoom, zoom);
                    this.lastZoom = zoom;
                }
                this.render();
            }
        });

        // Watch for object changes to re-render
        effect(() => {
            this.objects(); // Track objects signal
            if (this.canvasRef) {
                this.render();
            }
        });

        effect(() => {
            this.activeSide();
            if (this.canvasRef) {
                this.render();
            }
        });
    }

    ngAfterViewInit(): void {
        const canvas = this.canvasRef.nativeElement;
        this.ctx = canvas.getContext('2d')!;
        if (this.canvasPreviewRef) {
            this.previewCtx = this.canvasPreviewRef.nativeElement.getContext('2d')!;
        }
        this.resizeCanvas();
        this.render();

        // Listen for canvas rerender events (e.g., from SVG layer updates)
        window.addEventListener('canvas-rerender', ((event: CustomEvent) => {
            if (event.detail?.clearCache) {
                // Clear SVG cache when layers are updated
                const svgObjects = this.allObjects().filter(obj => obj.type === 'svg');
                svgObjects.forEach(obj => {
                    this.imageCache.delete(obj.id);
                });
            }
            this.render();
        }) as EventListener);
    }

    ngOnDestroy(): void {
        if (this.svgHitTestContainer && this.svgHitTestContainer.parentElement) {
            this.svgHitTestContainer.parentElement.removeChild(this.svgHitTestContainer);
        }
    }

    getToolIcon(): string {
        const tool = this.currentTool();
        const shapeTools = [...SHAPE_TOOLS];
        const allTools = [...TOOLS, ...shapeTools];
        if (tool === 'select') return '↖';
        const toolDef = allTools.find(t => t.type === tool);
        return toolDef?.icon || '→';
    }

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
        const cmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;

        if (cmdOrCtrl && event.key === 'z' && !event.shiftKey) {
            event.preventDefault();
            this.canvasService.undo();
            this.render();
        } else if (cmdOrCtrl && event.key === 'z' && event.shiftKey) {
            event.preventDefault();
            this.canvasService.redo();
            this.render();
        } else if (cmdOrCtrl && event.key === 'c') {
            event.preventDefault();
            this.canvasService.copySelected();
        } else if (cmdOrCtrl && event.key === 'v') {
            event.preventDefault();
            this.canvasService.paste();
            this.render();
        } else if (cmdOrCtrl && event.key === 'd') {
            event.preventDefault();
            this.canvasService.duplicateSelected();
            this.render();
        } else if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();
            this.canvasService.deleteObjects(this.selectedIds());
            this.render();
        } else if (cmdOrCtrl && event.key === 'g') {
            event.preventDefault();
            this.canvasService.groupSelected();
            this.render();
        }

        // Don't switch tools if user is typing in textarea/input
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLTextAreaElement || activeElement instanceof HTMLInputElement) {
            return;
        }

        // Tool shortcuts
        const key = event.key.toLowerCase();
        if (key === 'v') this.canvasService.setCurrentTool('select');
        else if (key === 'r') this.canvasService.setCurrentTool('rectangle');
        else if (key === 'o') this.canvasService.setCurrentTool('circle');
        else if (key === 'l') this.canvasService.setCurrentTool('line');
        else if (key === 't') this.canvasService.setCurrentTool('text');
    }

    onDoubleClick(event: MouseEvent): void {
        const point = this.getCanvasPoint(event);
        const clickedObject = this.findObjectAtPoint(point);
        if (!clickedObject || clickedObject.type !== 'text') return;

        // Select the text object - properties panel will auto-focus content textarea
        this.canvasService.selectObject(clickedObject.id);
        this.render();
    }

    private resizeCanvas(): void {
        if (this.isResizing) return;

        const canvas = this.canvasRef.nativeElement;
        const size = this.canvasSize();

        // Only resize if dimensions actually changed
        if (canvas.width !== size.width || canvas.height !== size.height) {
            this.isResizing = true;
            canvas.width = size.width;
            canvas.height = size.height;
            if (this.canvasPreviewRef) {
                const preview = this.canvasPreviewRef.nativeElement;
                preview.width = size.width;
                preview.height = size.height;
            }
            this.isResizing = false;
        }
    }

    isSplitView(): boolean {
        return this.canvasSizeType() === 'visiting-card';
    }

    private autoFitIfNeeded(key: string): void {
        if (!this.canvasContainerRef || this.lastAutoFitKey === key) return;
        const container = this.canvasContainerRef.nativeElement;
        const rect = container.getBoundingClientRect();
        const size = this.canvasSize();

        const availableWidth = Math.max(200, rect.width - 140);
        const availableHeight = Math.max(200, rect.height - 140);
        const widthRatio = availableWidth / size.width;
        const heightRatio = availableHeight / size.height;
        const containerRatio = availableWidth / availableHeight;
        const canvasRatio = size.width / size.height;
        const fitZoom = canvasRatio < containerRatio ? widthRatio : heightRatio;
        const nextZoom = Math.min(4, Math.max(0.2, fitZoom));

        this.canvasService.setZoom(nextZoom);
        this.canvasService.setPan({ x: 0, y: 0 });
        this.lastAutoFitKey = key;
    }

    private centerCanvasInView(): void {
        if (!this.canvasContainerRef) return;
        const container = this.canvasContainerRef.nativeElement;
        requestAnimationFrame(() => {
            const maxLeft = Math.max(0, container.scrollWidth - container.clientWidth);
            const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
            const zoom = this.zoom();
            const size = this.canvasSize();
            const scaledHeight = size.height * zoom;

            container.scrollLeft = Math.round(maxLeft / 2);
            container.scrollTop = scaledHeight > container.clientHeight ? 0 : Math.round(maxTop / 2);
        });
    }

    private adjustScrollForZoom(previousZoom: number, nextZoom: number): void {
        if (!this.canvasContainerRef || previousZoom === nextZoom) return;
        const container = this.canvasContainerRef.nativeElement;
        const centerX = container.clientWidth / 2;
        const centerY = container.clientHeight / 2;
        const scale = nextZoom / previousZoom;

        container.scrollLeft = Math.round((container.scrollLeft + centerX) * scale - centerX);
        container.scrollTop = Math.round((container.scrollTop + centerY) * scale - centerY);
    }

    isCanvasTall(): boolean {
        if (!this.canvasContainerRef) return false;
        const container = this.canvasContainerRef.nativeElement;
        const zoom = this.zoom();
        const size = this.canvasSize();
        const extraSpace = 120;
        return size.height * zoom + extraSpace > container.clientHeight;
    }

    onMouseDown(event: MouseEvent): void {
        const point = this.getCanvasPoint(event);
        this.dragState.isDragging = true;
        this.dragState.startPoint = point;
        this.dragState.currentPoint = point;
        this.dragState.points = undefined;

        const tool = this.currentTool();

        if (tool === 'select') {
            const clickedObject = this.findObjectAtPoint(point);
            if (clickedObject) {
                // Check if click is on a resize handle
                const handle = this.hitTestHandle(point, clickedObject);
                if (handle >= 0) {
                    this.dragState.isResizing = true;
                    this.dragState.resizeHandle = handle;
                    this.dragState.resizeStart = point;
                    this.dragState.resizingObjectId = clickedObject.id;
                    this.dragState.originalPosition = { ...clickedObject.position };
                    this.dragState.originalSize = { ...clickedObject.size };
                    this.canvasService.selectObject(clickedObject.id);
                    this.render();
                    return;
                }

                const isCmdCtrl = event.metaKey || event.ctrlKey;
                if (isCmdCtrl && clickedObject.type === 'svg') {
                    const layerId = this.findSVGLayerAtPoint(clickedObject as any, point);
                    if (layerId) {
                        this.canvasService.selectObject(clickedObject.id);
                        this.canvasService.updateObject(clickedObject.id, { selectedLayerId: layerId } as any, true);
                        this.dragState.isDragging = false;
                        this.dragState.draggedObjectId = undefined;
                        this.render();
                        return;
                    }
                    this.canvasService.updateObject(clickedObject.id, { selectedLayerId: undefined } as any, true);
                } else if (clickedObject.type === 'svg') {
                    this.canvasService.updateObject(clickedObject.id, { selectedLayerId: undefined } as any, true);
                }

                // Select clicked object without clearing previous selection (allows drag to move)
                if (!event.shiftKey && !this.selectedIds().includes(clickedObject.id)) {
                    this.canvasService.selectObject(clickedObject.id);
                } else if (event.shiftKey) {
                    this.canvasService.selectObject(clickedObject.id, true);
                }
                this.dragState.draggedObjectId = clickedObject.id;
            } else {
                // Start selection box
                this.dragState.isSelectionBox = true;
                this.canvasService.clearSelection();
            }
            this.render();
            return;
        }

        // Initialize drawing state for freehand/line/polygon tools
        if (tool === 'pen' || tool === 'line' || tool === 'polygon') {
            this.dragState.points = [point];
        }
    }

    onMouseMove(event: MouseEvent): void {
        if (!this.dragState.isDragging) return;

        const point = this.getCanvasPoint(event);
        this.dragState.currentPoint = point;

        // Handle resizing
        if (this.dragState.isResizing && this.dragState.resizingObjectId) {
            const id = this.dragState.resizingObjectId;
            const obj = this.objects().find(o => o.id === id);

            if (!obj || !this.dragState.originalPosition || !this.dragState.originalSize || !this.dragState.resizeStart) {
                return;
            }

            const dx = point.x - this.dragState.resizeStart.x;
            const dy = point.y - this.dragState.resizeStart.y;
            const handle = this.dragState.resizeHandle ?? -1;

            const originalPosition = this.dragState.originalPosition;
            const originalSize = this.dragState.originalSize;
            const originalRight = originalPosition.x + originalSize.width;
            const originalBottom = originalPosition.y + originalSize.height;
            const originalCenterX = originalPosition.x + originalSize.width / 2;
            const originalCenterY = originalPosition.y + originalSize.height / 2;

            const newPos = { ...originalPosition };
            const newSize = { ...originalSize };
            const minSize = 10;

            switch (handle) {
                case 0: // top-left
                    newPos.x = originalPosition.x + dx;
                    newPos.y = originalPosition.y + dy;
                    newSize.width = originalSize.width - dx;
                    newSize.height = originalSize.height - dy;
                    break;
                case 1: // top-right
                    newPos.y = originalPosition.y + dy;
                    newSize.width = originalSize.width + dx;
                    newSize.height = originalSize.height - dy;
                    break;
                case 2: // bottom-right
                    newSize.width = originalSize.width + dx;
                    newSize.height = originalSize.height + dy;
                    break;
                case 3: // bottom-left
                    newPos.x = originalPosition.x + dx;
                    newSize.width = originalSize.width - dx;
                    newSize.height = originalSize.height + dy;
                    break;
                case 4: // top-center
                    newPos.y = originalPosition.y + dy;
                    newSize.height = originalSize.height - dy;
                    break;
                case 5: // right-center
                    newSize.width = originalSize.width + dx;
                    break;
                case 6: // bottom-center
                    newSize.height = originalSize.height + dy;
                    break;
                case 7: // left-center
                    newPos.x = originalPosition.x + dx;
                    newSize.width = originalSize.width - dx;
                    break;
                default:
                    break;
            }

            if (event.shiftKey) {
                const aspect = originalSize.width / (originalSize.height || 1);
                const useWidth = Math.abs(newSize.width - originalSize.width) >= Math.abs(newSize.height - originalSize.height);
                if (useWidth) {
                    newSize.height = newSize.width / aspect;
                } else {
                    newSize.width = newSize.height * aspect;
                }
            }

            // Enforce minimum size
            if (newSize.width < minSize) newSize.width = minSize;
            if (newSize.height < minSize) newSize.height = minSize;

            if (event.shiftKey) {
                const affectsLeft = handle === 0 || handle === 3 || handle === 7;
                const affectsRight = handle === 1 || handle === 2 || handle === 5;
                const affectsTop = handle === 0 || handle === 1 || handle === 4;
                const affectsBottom = handle === 2 || handle === 3 || handle === 6;

                if (affectsLeft && !affectsRight) {
                    newPos.x = originalRight - newSize.width;
                } else if (!affectsLeft && affectsRight) {
                    newPos.x = originalPosition.x;
                } else {
                    newPos.x = originalCenterX - newSize.width / 2;
                }

                if (affectsTop && !affectsBottom) {
                    newPos.y = originalBottom - newSize.height;
                } else if (!affectsTop && affectsBottom) {
                    newPos.y = originalPosition.y;
                } else {
                    newPos.y = originalCenterY - newSize.height / 2;
                }
            }

            // Snap to grid if enabled
            const snappedPos = this.canvasService.snapToGridPoint(newPos);
            const snappedSize = { width: newSize.width, height: newSize.height };

            // Update object with new size and position
            const updates: any = { position: snappedPos, size: snappedSize };

            // For circles, also update radius
            if (obj.type === 'circle') {
                updates.radius = Math.min(snappedSize.width, snappedSize.height) / 2;
            }

            this.canvasService.updateObject(id, updates, true);
            this.render();
            return;
        }

        if (this.dragState.isPanning) {
            const dx = point.x - this.dragState.startPoint.x;
            const dy = point.y - this.dragState.startPoint.y;
            if (dx !== 0 || dy !== 0) {
                const currentPan = this.pan();
                this.canvasService.setPan({
                    x: currentPan.x + dx,
                    y: currentPan.y + dy
                });
                this.dragState.startPoint = point;
                this.render();
            }
            return;
        }

        const tool = this.currentTool();

        // For pen/line/polygon, append points while dragging
        if ((tool === 'pen' || tool === 'line' || tool === 'polygon') && this.dragState.points) {
            this.dragState.points.push(point);
            this.render();
            this.renderPreview();
            return;
        }

        if (tool === 'select' && this.dragState.draggedObjectId) {
            const dx = point.x - this.dragState.startPoint.x;
            const dy = point.y - this.dragState.startPoint.y;

            this.selectedIds().forEach(id => {
                const obj = this.objects().find(o => o.id === id);
                if (obj) {
                    const newPos = this.canvasService.snapToGridPoint({
                        x: obj.position.x + dx,
                        y: obj.position.y + dy
                    });
                    this.canvasService.updateObject(id, { position: newPos }, true);

                    // If this is a group, also move all its children
                    if (obj.type === 'group' && (obj as any).children) {
                        ((obj as any).children as string[]).forEach(childId => {
                            const child = this.objects().find(o => o.id === childId);
                            if (child) {
                                const childNewPos = this.canvasService.snapToGridPoint({
                                    x: child.position.x + dx,
                                    y: child.position.y + dy
                                });
                                this.canvasService.updateObject(childId, { position: childNewPos }, true);
                            }
                        });
                    }
                }
            });

            this.dragState.startPoint = point;
            this.render();
            return;
        }

        if (tool === 'select' && this.dragState.isSelectionBox) {
            this.render();
            this.renderPreview();
            return;
        }

        this.render();
        this.renderPreview();
    }

    onMouseUp(event: MouseEvent): void {
        if (!this.dragState.isDragging) return;

        const tool = this.currentTool();

        // Handle selection box
        if (tool === 'select' && this.dragState.isSelectionBox) {
            const start = this.dragState.startPoint;
            const end = this.dragState.currentPoint;
            const selectionBox = {
                x: Math.min(start.x, end.x),
                y: Math.min(start.y, end.y),
                width: Math.abs(end.x - start.x),
                height: Math.abs(end.y - start.y)
            };

            // Select all objects that intersect with selection box
            const selectedIds: string[] = [];
            this.objects().forEach(obj => {
                if (this.isObjectInBox(obj, selectionBox)) {
                    selectedIds.push(obj.id);
                }
            });

            if (selectedIds.length > 0) {
                this.canvasService.selectObjects(selectedIds);
            }

            this.dragState.isSelectionBox = false;
            this.dragState.isDragging = false;
            this.render();
            return;
        }

        // Save history for drag operations that used skipHistory
        if (tool === 'select' && this.dragState.draggedObjectId) {
            this.canvasService.saveHistoryNow('Move objects');
        }

        // Save history for resize operations
        if (this.dragState.isResizing) {
            this.canvasService.saveHistoryNow('Resize object');
        }

        if (!this.dragState.isPanning && tool !== 'select') {
            // Handle freehand and polygon specially
            if (tool === 'pen') {
                const points = this.dragState.points || [this.dragState.startPoint, this.dragState.currentPoint];
                if (points.length > 1) {
                    const minX = Math.min(...points.map(p => p.x));
                    const minY = Math.min(...points.map(p => p.y));
                    const maxX = Math.max(...points.map(p => p.x));
                    const maxY = Math.max(...points.map(p => p.y));
                    // Store points relative to object position
                    const relativePoints = points.map(p => ({ x: p.x - minX, y: p.y - minY }));
                    this.canvasService.addObject({
                        type: 'line',
                        position: { x: minX, y: minY },
                        size: { width: maxX - minX, height: maxY - minY },
                        points: relativePoints,
                        stroke: { color: { r: 50, g: 100, b: 200, a: 1 }, width: 2 }
                    } as any);
                }
            } else if (tool === 'polygon') {
                const points = this.dragState.points || [this.dragState.startPoint, this.dragState.currentPoint];
                if (points.length > 2) {
                    const minX = Math.min(...points.map(p => p.x));
                    const minY = Math.min(...points.map(p => p.y));
                    const maxX = Math.max(...points.map(p => p.x));
                    const maxY = Math.max(...points.map(p => p.y));
                    // Store points relative to object position
                    const relativePoints = points.map(p => ({ x: p.x - minX, y: p.y - minY }));
                    this.canvasService.addObject({
                        type: 'polygon',
                        position: { x: minX, y: minY },
                        size: { width: maxX - minX, height: maxY - minY },
                        points: relativePoints,
                        closed: true,
                        fill: { r: 100, g: 150, b: 255, a: 0.5 },
                        stroke: { color: { r: 50, g: 100, b: 200, a: 1 }, width: 2 }
                    } as any);
                }
            } else {
                this.createObject(tool);
            }
        }

        // Clear resizing state
        if (this.dragState.isResizing) {
            this.dragState.isResizing = false;
            this.dragState.resizeHandle = undefined;
            this.dragState.resizeStart = undefined;
            this.dragState.resizingObjectId = undefined;
            this.dragState.originalPosition = undefined;
            this.dragState.originalSize = undefined;
        }

        this.dragState.isDragging = false;
        this.dragState.isPanning = false;
        this.dragState.draggedObjectId = undefined;
        this.dragState.points = undefined;
        this.render();
    }

    onWheel(event: WheelEvent): void {
        if (!event.ctrlKey && !event.metaKey) {
            return;
        }

        event.preventDefault();
        const delta = event.deltaY > 0 ? 0.9 : 1.1;
        this.canvasService.setZoom(this.zoom() * delta);
        this.render();
    }

    canvasWrapperWidth(): number {
        const padding = 36;
        const size = this.canvasSize();
        return size.width * this.zoom() + padding;
    }

    canvasWrapperHeight(): number {
        const padding = 36;
        const size = this.canvasSize();
        return size.height * this.zoom() + padding;
    }

    canvasStyleWidth(): number {
        return this.canvasSize().width * this.zoom();
    }

    canvasStyleHeight(): number {
        return this.canvasSize().height * this.zoom();
    }

    svgOverlayObjects(): CanvasObject[] {
        return this.objects().filter(obj =>
            obj.type === 'svg' && obj.visible && !(obj as any).hasEditableText
        );
    }

    svgOverlayStyle(obj: CanvasObject): Record<string, string> {
        const zoom = this.zoom();
        const pan = this.pan();
        const left = (obj.position.x + pan.x) * zoom;
        const top = (obj.position.y + pan.y) * zoom;
        const width = obj.size.width * zoom;
        const height = obj.size.height * zoom;
        const rotation = obj.transform?.rotation ?? 0;
        const scaleX = obj.transform?.scaleX ?? 1;
        const scaleY = obj.transform?.scaleY ?? 1;

        return {
            left: `${left}px`,
            top: `${top}px`,
            width: `${width}px`,
            height: `${height}px`,
            transform: `rotate(${rotation}deg) scale(${scaleX}, ${scaleY})`,
            opacity: String(obj.opacity ?? 1),
            zIndex: String(obj.zIndex ?? 0)
        };
    }

    svgOverlaySrc(obj: CanvasObject): string | null {
        const svgString = (obj as any).svgContent as string | undefined;
        if (!svgString) return null;

        const normalizedSvg = this.normalizeSvgForRender(svgString, obj.size);
        const contentHash = this.simpleHash(normalizedSvg);
        const cached = this.svgOverlayCache.get(obj.id);
        if (cached && cached.hash === contentHash) {
            return cached.url;
        }

        const encoded = btoa(unescape(encodeURIComponent(normalizedSvg)));
        const url = `data:image/svg+xml;base64,${encoded}`;
        this.svgOverlayCache.set(obj.id, { hash: contentHash, url });
        return url;
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'copy';
        }
    }

    async onDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        const rawUrl = event.dataTransfer?.getData('text/plain') || event.dataTransfer?.getData('text/uri-list');
        const url = rawUrl?.split('\n')[0]?.trim();
        if (!url) return;

        try {
            const response = await fetch(url);
            if (!response.ok) return;
            const svgText = await response.text();
            const { svgContent, size, layers } = this.parseSVGContent(svgText);
            const point = this.getCanvasPoint(event as unknown as MouseEvent);
            const position = this.canvasService.snapToGridPoint(point);

            this.canvasService.addObject({
                type: 'svg',
                svgContent,
                originalSize: { ...size },
                size: { ...size },
                position,
                layers
            } as any);
            this.render();
        } catch (error) {
            console.error('Failed to drop SVG:', error);
        }
    }

    private createObject(tool: string): void {
        const start = this.dragState.startPoint;
        const end = this.dragState.currentPoint;

        const position = this.canvasService.snapToGridPoint({
            x: Math.min(start.x, end.x),
            y: Math.min(start.y, end.y)
        });

        const size = {
            width: Math.abs(end.x - start.x),
            height: Math.abs(end.y - start.y)
        };

        const defaultFill: RGBA = { r: 100, g: 150, b: 255, a: 1 };
        const defaultStroke = {
            color: { r: 50, g: 100, b: 200, a: 1 } as RGBA,
            width: 2
        };

        switch (tool) {
            case 'rectangle':
                this.canvasService.addObject({
                    type: 'rectangle',
                    position,
                    size,
                    fill: defaultFill,
                    stroke: defaultStroke,
                    cornerRadius: 0
                } as any);
                break;

            case 'circle': {
                const radius = Math.min(size.width, size.height) / 2;
                this.canvasService.addObject({
                    type: 'circle',
                    position,
                    size: { width: radius * 2, height: radius * 2 },
                    radius,
                    fill: defaultFill,
                    stroke: defaultStroke
                } as any);
                break;
            }

            case 'ellipse': {
                this.canvasService.addObject({
                    type: 'ellipse',
                    position,
                    size,
                    fill: defaultFill,
                    stroke: defaultStroke
                } as any);
                break;
            }

            case 'triangle':
            case 'diamond':
            case 'pentagon':
            case 'hexagon':
            case 'star':
            case 'heart':
            case 'check':
            case 'arrow':
            case 'cloud':
                this.canvasService.addObject({
                    type: tool as any,
                    position,
                    size,
                    fill: defaultFill,
                    stroke: defaultStroke,
                    cornerRadius: 0
                } as any);
                break;

            case 'line': {
                // Store points relative to object position
                const relativeStart = { x: 0, y: 0 };
                const relativeEnd = { x: end.x - start.x, y: end.y - start.y };
                this.canvasService.addObject({
                    type: 'line',
                    position,
                    size,
                    points: [relativeStart, relativeEnd],
                    stroke: defaultStroke
                } as any);
                break;
            }

            case 'text': {
                // Create a new text object with default properties
                const newTextObject = {
                    type: 'text' as const,
                    position,
                    size: { width: 200, height: 50 },
                    content: 'Text', // Default text content
                    fill: { r: 0, g: 0, b: 0, a: 1 },
                    cornerRadius: 0,
                    textStyle: {
                        fontFamily: 'Arial',
                        fontSize: 16,
                        fontWeight: 'normal',
                        fontStyle: 'normal',
                        textAlign: 'left',
                        textDecoration: 'none',
                        lineHeight: 1.5, // Supports multi-line text
                        letterSpacing: 0
                    }
                };

                // Add and select the text object
                const id = this.canvasService.addObject(newTextObject as any);
                this.canvasService.selectObject(id);
                // Auto-switch to select tool after creating text
                this.canvasService.setCurrentTool('select');
                this.render();
                break;
            }

            case 'frame':
                this.canvasService.addObject({
                    type: 'frame',
                    position,
                    size,
                    children: [],
                    clipContent: true,
                    cornerRadius: 0,
                    fill: { r: 255, g: 255, b: 255, a: 0.05 },
                    stroke: { color: { r: 150, g: 150, b: 150, a: 0.5 }, width: 1 }
                } as any);
                break;
        }
    }

    private render(): void {
        if (!this.ctx) return;

        this.renderScene(this.ctx, this.objects(), true);

        if (this.isSplitView() && this.previewCtx && this.canvasPreviewRef) {
            const otherSide = this.activeSide() === 'front' ? 'back' : 'front';
            const otherObjects = this.allObjects().filter(obj => (obj.side || 'front') === otherSide);
            this.renderScene(this.previewCtx, otherObjects, false);
        }
    }

    private renderScene(ctx: CanvasRenderingContext2D, objects: CanvasObject[], drawSelection: boolean): void {
        const canvas = ctx.canvas;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.save();

        const pan = this.pan();
        ctx.translate(pan.x, pan.y);

        if (this.gridVisible()) {
            const previous = this.ctx;
            this.ctx = ctx;
            this.drawGrid();
            this.ctx = previous;
        }

        const sorted = [...objects].sort((a, b) => a.zIndex - b.zIndex);
        const previous = this.ctx;
        this.ctx = ctx;
        sorted.forEach(obj => {
            if (obj.visible) {
                this.drawObject(obj);
            }
        });

        if (drawSelection) {
            this.drawSelection();
        }

        this.ctx = previous;
        ctx.restore();
    }

    private renderPreview(): void {
        if (!this.dragState.isDragging) return;

        const start = this.dragState.startPoint;
        const end = this.dragState.currentPoint;

        this.ctx.save();
        this.ctx.strokeStyle = 'rgba(100, 150, 255, 0.8)';
        this.ctx.lineWidth = 2;
        this.ctx.setLineDash([5, 5]);

        const zoom = this.zoom();
        const pan = this.pan();
        this.ctx.translate(pan.x, pan.y);
        this.ctx.scale(zoom, zoom);

        const tool = this.currentTool();

        // Draw selection box
        if (tool === 'select' && this.dragState.isSelectionBox) {
            this.ctx.fillStyle = 'rgba(100, 150, 255, 0.15)';
            this.ctx.strokeStyle = 'rgba(100, 150, 255, 0.8)';
            this.ctx.fillRect(
                Math.min(start.x, end.x),
                Math.min(start.y, end.y),
                Math.abs(end.x - start.x),
                Math.abs(end.y - start.y)
            );
            this.ctx.strokeRect(
                Math.min(start.x, end.x),
                Math.min(start.y, end.y),
                Math.abs(end.x - start.x),
                Math.abs(end.y - start.y)
            );
            this.ctx.restore();
            return;
        }

        if (tool === 'pen' && this.dragState.points && this.dragState.points.length > 0) {
            const pts = this.dragState.points;
            this.ctx.beginPath();
            this.ctx.moveTo(pts[0].x, pts[0].y);
            for (let i = 1; i < pts.length; i++) {
                this.ctx.lineTo(pts[i].x, pts[i].y);
            }
            this.ctx.stroke();
        } else if (tool === 'line') {
            this.ctx.beginPath();
            this.ctx.moveTo(start.x, start.y);
            this.ctx.lineTo(end.x, end.y);
            this.ctx.stroke();
        } else if (tool === 'polygon') {
            const pts = this.dragState.points || [start, end];
            if (pts.length > 1) {
                this.ctx.beginPath();
                this.ctx.moveTo(pts[0].x, pts[0].y);
                for (let i = 1; i < pts.length; i++) this.ctx.lineTo(pts[i].x, pts[i].y);
                this.ctx.closePath();
                this.ctx.stroke();
            }
        } else if (
            tool === 'triangle' ||
            tool === 'diamond' ||
            tool === 'pentagon' ||
            tool === 'hexagon' ||
            tool === 'star' ||
            tool === 'arrow'
        ) {
            const position = this.canvasService.snapToGridPoint({
                x: Math.min(start.x, end.x),
                y: Math.min(start.y, end.y)
            });
            const size = {
                width: Math.abs(end.x - start.x),
                height: Math.abs(end.y - start.y)
            };
            this.drawPolygon(this.getShapePoints(tool, position, size));
        } else if (tool === 'heart') {
            const position = this.canvasService.snapToGridPoint({
                x: Math.min(start.x, end.x),
                y: Math.min(start.y, end.y)
            });
            const size = {
                width: Math.abs(end.x - start.x),
                height: Math.abs(end.y - start.y)
            };
            this.drawHeart(position, size);
        } else if (tool === 'cloud') {
            const position = this.canvasService.snapToGridPoint({
                x: Math.min(start.x, end.x),
                y: Math.min(start.y, end.y)
            });
            const size = {
                width: Math.abs(end.x - start.x),
                height: Math.abs(end.y - start.y)
            };
            this.drawCloud(position, size);
        } else if (tool === 'check') {
            const position = this.canvasService.snapToGridPoint({
                x: Math.min(start.x, end.x),
                y: Math.min(start.y, end.y)
            });
            const size = {
                width: Math.abs(end.x - start.x),
                height: Math.abs(end.y - start.y)
            };
            this.drawCheck(position, size);
        } else {
            this.ctx.strokeRect(
                Math.min(start.x, end.x),
                Math.min(start.y, end.y),
                Math.abs(end.x - start.x),
                Math.abs(end.y - start.y)
            );
        }

        this.ctx.restore();
    }

    private drawGrid(): void {
        const canvas = this.canvasRef.nativeElement;
        const gridSize = this.gridSize();
        const zoom = this.zoom();
        const pan = this.pan();

        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        this.ctx.lineWidth = 1 / zoom;

        const startX = Math.floor(-pan.x / zoom / gridSize) * gridSize;
        const startY = Math.floor(-pan.y / zoom / gridSize) * gridSize;
        const endX = startX + canvas.width / zoom + gridSize;
        const endY = startY + canvas.height / zoom + gridSize;

        for (let x = startX; x < endX; x += gridSize) {
            this.ctx.beginPath();
            this.ctx.moveTo(x, startY);
            this.ctx.lineTo(x, endY);
            this.ctx.stroke();
        }

        for (let y = startY; y < endY; y += gridSize) {
            this.ctx.beginPath();
            this.ctx.moveTo(startX, y);
            this.ctx.lineTo(endX, y);
            this.ctx.stroke();
        }
    }

    private drawObject(obj: CanvasObject): void {
        this.ctx.save();

        this.ctx.globalAlpha = obj.opacity;

        // Apply transform
        const centerX = obj.position.x + obj.size.width / 2;
        const centerY = obj.position.y + obj.size.height / 2;
        this.ctx.translate(centerX, centerY);
        this.ctx.rotate((obj.transform.rotation * Math.PI) / 180);
        this.ctx.scale(obj.transform.scaleX, obj.transform.scaleY);
        this.ctx.translate(-centerX, -centerY);

        // Set fill and stroke
        if (obj.fill) {
            this.ctx.fillStyle = this.rgbaToString(obj.fill);
        }
        if (obj.stroke) {
            this.ctx.strokeStyle = this.rgbaToString(obj.stroke.color);
            this.ctx.lineWidth = obj.stroke.width;
        }

        switch (obj.type) {
            case 'rectangle':
                if (obj.cornerRadius) {
                    this.drawRoundedRect(obj.position, obj.size, obj.cornerRadius);
                } else {
                    this.ctx.fillRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                    if (obj.stroke) {
                        this.ctx.strokeRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                    }
                }
                break;

            case 'circle': {
                const circleCx = obj.position.x + obj.size.width / 2;
                const circleCy = obj.position.y + obj.size.height / 2;
                this.ctx.beginPath();
                this.ctx.arc(circleCx, circleCy, obj.radius, 0, Math.PI * 2);
                if (obj.fill) this.ctx.fill();
                if (obj.stroke) this.ctx.stroke();
                break;
            }

            case 'ellipse': {
                const ellCx = obj.position.x + obj.size.width / 2;
                const ellCy = obj.position.y + obj.size.height / 2;
                this.ctx.beginPath();
                this.ctx.ellipse(ellCx, ellCy, obj.size.width / 2, obj.size.height / 2, 0, 0, Math.PI * 2);
                if (obj.fill) this.ctx.fill();
                if (obj.stroke) this.ctx.stroke();
                break;
            }

            case 'triangle':
            case 'diamond':
            case 'pentagon':
            case 'hexagon':
            case 'star':
            case 'arrow': {
                const points = this.getShapePoints(obj.type, obj.position, obj.size);
                const cornerRadius = (obj as any).cornerRadius || 0;
                if (cornerRadius > 0) {
                    this.drawRoundedPolygon(points, cornerRadius);
                } else {
                    this.drawPolygon(points);
                }
                break;
            }

            case 'heart':
                this.drawHeart(obj.position, obj.size, (obj as any).cornerRadius);
                break;

            case 'check':
                this.drawCheck(obj.position, obj.size, (obj as any).cornerRadius);
                break;

            case 'cloud':
                this.drawCloud(obj.position, obj.size, (obj as any).cornerRadius);
                break;

            case 'line':
            case 'polygon':
                if (obj.points.length > 1) {
                    this.ctx.beginPath();
                    // Render points relative to object position
                    this.ctx.moveTo(obj.position.x + obj.points[0].x, obj.position.y + obj.points[0].y);
                    for (let i = 1; i < obj.points.length; i++) {
                        this.ctx.lineTo(obj.position.x + obj.points[i].x, obj.position.y + obj.points[i].y);
                    }
                    if (obj.type === 'polygon' && obj.closed) {
                        this.ctx.closePath();
                        if (obj.fill) this.ctx.fill();
                    }
                    if (obj.stroke) this.ctx.stroke();
                }
                break;

            case 'text': {
                const cornerRadius = (obj as any).cornerRadius || 0;

                // Draw background with corner radius if needed
                if (cornerRadius > 0) {
                    // Save current fill style and restore after background
                    const prevFillStyle = this.ctx.fillStyle;
                    if (obj.fill) {
                        this.drawRoundedRect(obj.position, obj.size, cornerRadius);
                    }
                    this.ctx.fillStyle = prevFillStyle;
                }

                this.ctx.font = `${obj.textStyle.fontStyle} ${obj.textStyle.fontWeight} ${obj.textStyle.fontSize}px ${obj.textStyle.fontFamily}`;
                this.ctx.textAlign = obj.textStyle.textAlign;
                this.ctx.textBaseline = 'top';
                if (obj.fill) {
                    // Handle multi-line text
                    const lines = obj.content.split('\n');
                    const lineHeight = obj.textStyle.fontSize * obj.textStyle.lineHeight;
                    lines.forEach((line, index) => {
                        this.ctx.fillText(line, obj.position.x, obj.position.y + (index * lineHeight));
                    });
                }
                break;
            }

            case 'image': {
                // Render image if loaded, otherwise load and cache it
                try {
                    const cornerRadius = (obj as any).cornerRadius || 0;
                    const key = obj.id || (obj as any).src;
                    const cached = this.imageCache.get(key as string);

                    const drawImageWithRadius = (image: any) => {
                        if (cornerRadius > 0) {
                            this.ctx.save();
                            this.createRoundedRectPath(obj.position, obj.size, cornerRadius);
                            this.ctx.clip();
                        }

                        if ((image as ImageBitmap).close !== undefined) {
                            this.ctx.drawImage(image as ImageBitmap, obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                        } else {
                            this.ctx.drawImage(image as HTMLImageElement, obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                        }

                        if (cornerRadius > 0) {
                            this.ctx.restore();
                        }
                    };

                    if (cached) {
                        drawImageWithRadius(cached);
                    } else if ((obj as any).src) {
                        const img = new Image();
                        img.crossOrigin = 'anonymous';
                        img.onload = () => {
                            this.imageCache.set(key as string, img);
                            this.render();
                        };
                        img.onerror = (e) => {
                            console.error('Image load failed', e);
                        };
                        img.src = (obj as any).src;
                    } else {
                        this.ctx.strokeRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                    }
                } catch (e) {
                    this.ctx.strokeRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                }
                break;
            }

            case 'svg':
                try {
                    const key = obj.id;
                    const svgString = (obj as any).svgContent as string | undefined;
                    const svgSrc = (obj as any).src as string | undefined;
                    let didDraw = false;

                    if (svgString) {
                        const normalizedSvg = this.normalizeSvgForRender(svgString, obj.size);
                        const contentHash = this.simpleHash(normalizedSvg);
                        const cached = this.imageCache.get(key);
                        const cachedHash = (cached as any)?._svgHash;

                        if (cached && cachedHash === contentHash) {
                            if ((cached as ImageBitmap).close !== undefined) {
                                this.ctx.drawImage(cached as ImageBitmap, obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                                didDraw = true;
                            } else {
                                const htmlImage = cached as HTMLImageElement;
                                if (htmlImage.complete && htmlImage.naturalWidth > 0) {
                                    this.ctx.drawImage(htmlImage, obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                                    didDraw = true;
                                }
                            }
                        } else {
                            this.loadSvgImage(normalizedSvg, key, contentHash);
                        }
                    } else if (svgSrc) {
                        const srcKey = `${key}::svg-src`;
                        const cachedSrc = this.imageCache.get(srcKey);
                        if (cachedSrc) {
                            if ((cachedSrc as ImageBitmap).close !== undefined) {
                                this.ctx.drawImage(cachedSrc as ImageBitmap, obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                                didDraw = true;
                            } else {
                                const htmlImage = cachedSrc as HTMLImageElement;
                                if (htmlImage.complete && htmlImage.naturalWidth > 0) {
                                    this.ctx.drawImage(htmlImage, obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                                    didDraw = true;
                                }
                            }
                        } else {
                            const img = new Image();
                            img.onload = () => {
                                this.imageCache.set(srcKey, img);
                                this.render();
                            };
                            img.onerror = () => {
                                this.imageCache.delete(srcKey);
                            };
                            img.src = svgSrc;
                        }
                    }

                    if (!didDraw) {
                        this.ctx.save();
                        this.ctx.strokeStyle = '#2f80ed';
                        this.ctx.lineWidth = 1;
                        this.ctx.strokeRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                        this.ctx.restore();
                    }
                } catch (e) {
                    this.ctx.strokeRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                }
                break;

            case 'frame': {
                const cornerRadius = (obj as any).cornerRadius || 0;
                if (cornerRadius > 0) {
                    this.drawRoundedRect(obj.position, obj.size, cornerRadius);
                } else {
                    this.ctx.strokeRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                    if (obj.fill) {
                        this.ctx.fillRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);
                    }
                }
                break;
            }
        }

        this.ctx.restore();
    }

    private drawSelection(): void {
        const selectedIds = this.selectedIds();
        if (selectedIds.length === 0) return;

        this.ctx.save();
        this.ctx.strokeStyle = '#0078d4';
        this.ctx.lineWidth = 2 / this.zoom();
        this.ctx.setLineDash([5 / this.zoom(), 5 / this.zoom()]);

        selectedIds.forEach(id => {
            const obj = this.objects().find(o => o.id === id);
            if (obj) {
                this.ctx.strokeRect(obj.position.x, obj.position.y, obj.size.width, obj.size.height);

                // Draw resize handles
                this.drawResizeHandles(obj);
            }
        });

        this.ctx.restore();
    }

    private drawResizeHandles(obj: CanvasObject): void {
        const handleSize = 8 / this.zoom();
        const positions = [
            { x: obj.position.x, y: obj.position.y },
            { x: obj.position.x + obj.size.width, y: obj.position.y },
            { x: obj.position.x + obj.size.width, y: obj.position.y + obj.size.height },
            { x: obj.position.x, y: obj.position.y + obj.size.height },
            { x: obj.position.x + obj.size.width / 2, y: obj.position.y },
            { x: obj.position.x + obj.size.width, y: obj.position.y + obj.size.height / 2 },
            { x: obj.position.x + obj.size.width / 2, y: obj.position.y + obj.size.height },
            { x: obj.position.x, y: obj.position.y + obj.size.height / 2 }
        ];

        this.ctx.fillStyle = '#ffffff';
        this.ctx.strokeStyle = '#0078d4';
        this.ctx.lineWidth = 1 / this.zoom();
        this.ctx.setLineDash([]);

        positions.forEach(pos => {
            this.ctx.fillRect(pos.x - handleSize / 2, pos.y - handleSize / 2, handleSize, handleSize);
            this.ctx.strokeRect(pos.x - handleSize / 2, pos.y - handleSize / 2, handleSize, handleSize);
        });
    }

    // Return handle index if point is on a resize handle, otherwise -1
    private hitTestHandle(point: Point, obj: CanvasObject): number {
        const handleSize = 8 / this.zoom();
        const tolerance = Math.max(handleSize / 2, 10); // At least 10px tolerance
        const positions = [
            { x: obj.position.x, y: obj.position.y },
            { x: obj.position.x + obj.size.width, y: obj.position.y },
            { x: obj.position.x + obj.size.width, y: obj.position.y + obj.size.height },
            { x: obj.position.x, y: obj.position.y + obj.size.height },
            { x: obj.position.x + obj.size.width / 2, y: obj.position.y },
            { x: obj.position.x + obj.size.width, y: obj.position.y + obj.size.height / 2 },
            { x: obj.position.x + obj.size.width / 2, y: obj.position.y + obj.size.height },
            { x: obj.position.x, y: obj.position.y + obj.size.height / 2 }
        ];

        for (let i = 0; i < positions.length; i++) {
            const pos = positions[i];
            if (
                point.x >= pos.x - tolerance &&
                point.x <= pos.x + tolerance &&
                point.y >= pos.y - tolerance &&
                point.y <= pos.y + tolerance
            ) {
                return i;
            }
        }
        return -1;
    }

    private getShapePoints(type: string, position: Point, size: Size): Point[] {
        const centerX = position.x + size.width / 2;
        const centerY = position.y + size.height / 2;
        const radius = Math.min(size.width, size.height) / 2;

        switch (type) {
            case 'triangle':
                return [
                    { x: centerX, y: position.y },
                    { x: position.x + size.width, y: position.y + size.height },
                    { x: position.x, y: position.y + size.height }
                ];
            case 'diamond':
                return [
                    { x: centerX, y: position.y },
                    { x: position.x + size.width, y: centerY },
                    { x: centerX, y: position.y + size.height },
                    { x: position.x, y: centerY }
                ];
            case 'pentagon':
                return this.createRegularPolygonPoints(5, centerX, centerY, radius, -Math.PI / 2);
            case 'hexagon':
                return this.createRegularPolygonPoints(6, centerX, centerY, radius, -Math.PI / 2);
            case 'star':
                return this.createStarPoints(5, centerX, centerY, radius, radius * 0.5, -Math.PI / 2);
            case 'arrow':
                return [
                    { x: position.x, y: position.y + size.height * 0.3 },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.3 },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.05 },
                    { x: position.x + size.width, y: centerY },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.95 },
                    { x: position.x + size.width * 0.6, y: position.y + size.height * 0.7 },
                    { x: position.x, y: position.y + size.height * 0.7 }
                ];
            default:
                return this.createRegularPolygonPoints(4, centerX, centerY, radius, -Math.PI / 4);
        }
    }

    private createRegularPolygonPoints(sides: number, cx: number, cy: number, radius: number, rotation: number): Point[] {
        const points: Point[] = [];
        for (let i = 0; i < sides; i++) {
            const angle = rotation + (i * 2 * Math.PI) / sides;
            points.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
        }
        return points;
    }

    private createStarPoints(points: number, cx: number, cy: number, outerRadius: number, innerRadius: number, rotation: number): Point[] {
        const result: Point[] = [];
        const totalPoints = points * 2;
        for (let i = 0; i < totalPoints; i++) {
            const radius = i % 2 === 0 ? outerRadius : innerRadius;
            const angle = rotation + (i * Math.PI) / points;
            result.push({ x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) });
        }
        return result;
    }

    private drawPolygon(points: Point[]): void {
        if (points.length < 2) return;
        this.ctx.beginPath();
        this.ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            this.ctx.lineTo(points[i].x, points[i].y);
        }
        this.ctx.closePath();
        if (this.ctx.fillStyle) this.ctx.fill();
        if (this.ctx.strokeStyle) this.ctx.stroke();
    }

    private drawHeart(position: Point, size: Size, cornerRadius?: number): void {
        const x = position.x;
        const y = position.y;
        const w = size.width;
        const h = size.height;

        this.ctx.beginPath();
        this.ctx.moveTo(x + w / 2, y + h);
        this.ctx.bezierCurveTo(
            x + w * 0.9,
            y + h * 0.85,
            x + w,
            y + h * 0.55,
            x + w * 0.8,
            y + h * 0.3
        );
        this.ctx.bezierCurveTo(
            x + w * 0.65,
            y + h * 0.05,
            x + w * 0.5,
            y + h * 0.1,
            x + w / 2,
            y + h * 0.25
        );
        this.ctx.bezierCurveTo(
            x + w * 0.5,
            y + h * 0.1,
            x + w * 0.35,
            y + h * 0.05,
            x + w * 0.2,
            y + h * 0.3
        );
        this.ctx.bezierCurveTo(
            x,
            y + h * 0.55,
            x + w * 0.1,
            y + h * 0.85,
            x + w / 2,
            y + h
        );
        this.ctx.closePath();
        if (this.ctx.fillStyle) this.ctx.fill();
        if (this.ctx.strokeStyle) this.ctx.stroke();
    }

    private drawCheck(position: Point, size: Size, cornerRadius?: number): void {
        const p1 = { x: position.x + size.width * 0.15, y: position.y + size.height * 0.55 };
        const p2 = { x: position.x + size.width * 0.4, y: position.y + size.height * 0.8 };
        const p3 = { x: position.x + size.width * 0.85, y: position.y + size.height * 0.25 };

        this.ctx.beginPath();

        if (cornerRadius && cornerRadius > 0) {
            // Calculate distances for smooth rounding
            const dist12 = Math.sqrt((p2.x - p1.x) ** 2 + (p2.y - p1.y) ** 2);
            const dist23 = Math.sqrt((p3.x - p2.x) ** 2 + (p3.y - p2.y) ** 2);

            // Clamp radius to prevent overlaps
            const offset1 = Math.min(cornerRadius, dist12 * 0.4);
            const offset2 = Math.min(cornerRadius, dist23 * 0.4);

            // Calculate direction vectors
            const dir12x = (p2.x - p1.x) / dist12;
            const dir12y = (p2.y - p1.y) / dist12;
            const dir23x = (p3.x - p2.x) / dist23;
            const dir23y = (p3.y - p2.y) / dist23;

            // Points before and after the middle vertex
            const beforeP2 = { x: p2.x - dir12x * offset1, y: p2.y - dir12y * offset1 };
            const afterP2 = { x: p2.x + dir23x * offset2, y: p2.y + dir23y * offset2 };

            // Draw the check with rounded corner at middle vertex
            this.ctx.moveTo(p1.x, p1.y);
            this.ctx.lineTo(beforeP2.x, beforeP2.y);
            this.ctx.quadraticCurveTo(p2.x, p2.y, afterP2.x, afterP2.y);
            this.ctx.lineTo(p3.x, p3.y);
        } else {
            // Draw straight check without rounding
            this.ctx.moveTo(p1.x, p1.y);
            this.ctx.lineTo(p2.x, p2.y);
            this.ctx.lineTo(p3.x, p3.y);
        }

        if (this.ctx.strokeStyle) this.ctx.stroke();
    }

    private drawCloud(position: Point, size: Size, cornerRadius?: number): void {
        const x = position.x;
        const y = position.y;
        const w = size.width;
        const h = size.height;

        this.ctx.beginPath();
        this.ctx.moveTo(x + w * 0.2, y + h * 0.6);
        this.ctx.bezierCurveTo(x + w * 0.05, y + h * 0.6, x + w * 0.05, y + h * 0.35, x + w * 0.2, y + h * 0.35);
        this.ctx.bezierCurveTo(x + w * 0.25, y + h * 0.2, x + w * 0.45, y + h * 0.15, x + w * 0.55, y + h * 0.28);
        this.ctx.bezierCurveTo(x + w * 0.7, y + h * 0.2, x + w * 0.9, y + h * 0.3, x + w * 0.9, y + h * 0.48);
        this.ctx.bezierCurveTo(x + w * 0.98, y + h * 0.5, x + w * 0.98, y + h * 0.68, x + w * 0.85, y + h * 0.7);
        this.ctx.bezierCurveTo(x + w * 0.8, y + h * 0.85, x + w * 0.6, y + h * 0.9, x + w * 0.5, y + h * 0.8);
        this.ctx.bezierCurveTo(x + w * 0.35, y + h * 0.9, x + w * 0.2, y + h * 0.85, x + w * 0.2, y + h * 0.6);
        this.ctx.closePath();
        if (this.ctx.fillStyle) this.ctx.fill();
        if (this.ctx.strokeStyle) this.ctx.stroke();
    }

    private drawRoundedRect(position: Point, size: Size, radius: number): void {
        this.ctx.beginPath();
        this.ctx.moveTo(position.x + radius, position.y);
        this.ctx.lineTo(position.x + size.width - radius, position.y);
        this.ctx.quadraticCurveTo(position.x + size.width, position.y, position.x + size.width, position.y + radius);
        this.ctx.lineTo(position.x + size.width, position.y + size.height - radius);
        this.ctx.quadraticCurveTo(position.x + size.width, position.y + size.height, position.x + size.width - radius, position.y + size.height);
        this.ctx.lineTo(position.x + radius, position.y + size.height);
        this.ctx.quadraticCurveTo(position.x, position.y + size.height, position.x, position.y + size.height - radius);
        this.ctx.lineTo(position.x, position.y + radius);
        this.ctx.quadraticCurveTo(position.x, position.y, position.x + radius, position.y);
        this.ctx.closePath();
        this.ctx.fill();
        if (this.ctx.strokeStyle) {
            this.ctx.stroke();
        }
    }

    private createRoundedRectPath(position: Point, size: Size, radius: number): void {
        this.ctx.beginPath();
        this.ctx.moveTo(position.x + radius, position.y);
        this.ctx.lineTo(position.x + size.width - radius, position.y);
        this.ctx.quadraticCurveTo(position.x + size.width, position.y, position.x + size.width, position.y + radius);
        this.ctx.lineTo(position.x + size.width, position.y + size.height - radius);
        this.ctx.quadraticCurveTo(position.x + size.width, position.y + size.height, position.x + size.width - radius, position.y + size.height);
        this.ctx.lineTo(position.x + radius, position.y + size.height);
        this.ctx.quadraticCurveTo(position.x, position.y + size.height, position.x, position.y + size.height - radius);
        this.ctx.lineTo(position.x, position.y + radius);
        this.ctx.quadraticCurveTo(position.x, position.y, position.x + radius, position.y);
        this.ctx.closePath();
    }

    private drawRoundedPolygon(points: Point[], radius: number): void {
        if (points.length < 2) return;

        this.ctx.beginPath();

        // Calculate the distance between two points
        const distance = (p1: Point, p2: Point): number => {
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            return Math.sqrt(dx * dx + dy * dy);
        };

        // Draw the polygon with rounded corners using quadratic curves
        const totalPoints = points.length;

        for (let i = 0; i < totalPoints; i++) {
            const prev = points[(i - 1 + totalPoints) % totalPoints];
            const curr = points[i];
            const next = points[(i + 1) % totalPoints];

            // Calculate distances to adjacent vertices
            const distToPrev = distance(prev, curr);
            const distToNext = distance(curr, next);

            // Clamp the radius offset to half the edge lengths to prevent overlaps
            const offsetIn = Math.min(radius, distToPrev * 0.4);
            const offsetOut = Math.min(radius, distToNext * 0.4);

            // Calculate direction vectors (normalized)
            const prevToCurrentDir = distToPrev > 0 ? {
                x: (curr.x - prev.x) / distToPrev,
                y: (curr.y - prev.y) / distToPrev
            } : { x: 0, y: 0 };

            const currentToNextDir = distToNext > 0 ? {
                x: (next.x - curr.x) / distToNext,
                y: (next.y - curr.y) / distToNext
            } : { x: 0, y: 0 };

            // Point on incoming edge (where we stop before the corner)
            const inPoint = {
                x: curr.x - prevToCurrentDir.x * offsetIn,
                y: curr.y - prevToCurrentDir.y * offsetIn
            };

            // Point on outgoing edge (where we continue after the corner)
            const outPoint = {
                x: curr.x + currentToNextDir.x * offsetOut,
                y: curr.y + currentToNextDir.y * offsetOut
            };

            // On first iteration, move to the start point
            if (i === 0) {
                this.ctx.moveTo(inPoint.x, inPoint.y);
            } else {
                // Line to the start of this corner
                this.ctx.lineTo(inPoint.x, inPoint.y);
            }

            // Draw a quadratic curve through the corner vertex to the next segment
            // Using the current vertex as the control point creates a smooth rounded corner
            this.ctx.quadraticCurveTo(curr.x, curr.y, outPoint.x, outPoint.y);
        }

        this.ctx.closePath();
        if (this.ctx.fillStyle) this.ctx.fill();
        if (this.ctx.strokeStyle) this.ctx.stroke();
    }

    private findObjectAtPoint(point: Point): CanvasObject | null {
        const objects = [...this.objects()].sort((a, b) => b.zIndex - a.zIndex);

        for (const obj of objects) {
            // Check if point is in object or on a resize handle
            if (this.isPointInObject(point, obj) || this.hitTestHandle(point, obj) >= 0) {
                return obj;
            }
        }

        return null;
    }

    private isPointInObject(point: Point, obj: CanvasObject): boolean {
        if (obj.type === 'text') {
            const textAlign = obj.textStyle?.textAlign ?? 'left';
            let left = obj.position.x;
            let right = obj.position.x + obj.size.width;

            if (textAlign === 'center') {
                left = obj.position.x - obj.size.width / 2;
                right = obj.position.x + obj.size.width / 2;
            } else if (textAlign === 'right') {
                left = obj.position.x - obj.size.width;
                right = obj.position.x;
            }

            return (
                point.x >= left &&
                point.x <= right &&
                point.y >= obj.position.y &&
                point.y <= obj.position.y + obj.size.height
            );
        }

        return (
            point.x >= obj.position.x &&
            point.x <= obj.position.x + obj.size.width &&
            point.y >= obj.position.y &&
            point.y <= obj.position.y + obj.size.height
        );
    }

    private isObjectInBox(obj: CanvasObject, box: { x: number; y: number; width: number; height: number }): boolean {
        // Check if object intersects with selection box
        return !(
            obj.position.x + obj.size.width < box.x ||
            obj.position.x > box.x + box.width ||
            obj.position.y + obj.size.height < box.y ||
            obj.position.y > box.y + box.height
        );
    }

    private getCanvasPoint(event: MouseEvent): Point {
        const canvas = this.canvasRef.nativeElement;
        const rect = canvas.getBoundingClientRect();
        const zoom = this.zoom();
        const pan = this.pan();

        return {
            x: (event.clientX - rect.left - pan.x) / zoom,
            y: (event.clientY - rect.top - pan.y) / zoom
        };
    }

    private rgbaToString(rgba: RGBA): string {
        return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
    }

    private getSvgHitTestContainer(): SVGSVGElement {
        if (!this.svgHitTestContainer) {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.setAttribute('width', '0');
            svg.setAttribute('height', '0');
            svg.style.position = 'absolute';
            svg.style.left = '-10000px';
            svg.style.top = '-10000px';
            svg.style.visibility = 'hidden';
            document.body.appendChild(svg);
            this.svgHitTestContainer = svg;
        }

        return this.svgHitTestContainer;
    }

    private escapeSvgId(id: string): string {
        return id.replace(/([ #;?%&,.+*~':"!^$\[\]()=>|/@])/g, '\\$1');
    }

    private findSVGLayerAtPoint(obj: any, point: Point): string | null {
        if (!obj.svgContent || !obj.layers || !obj.originalSize) return null;

        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(obj.svgContent, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');
        if (!svgElement) return null;

        const container = this.getSvgHitTestContainer();
        container.setAttribute('viewBox', `0 0 ${obj.originalSize.width} ${obj.originalSize.height}`);
        container.setAttribute('width', String(obj.originalSize.width));
        container.setAttribute('height', String(obj.originalSize.height));
        container.innerHTML = svgElement.innerHTML;

        const scaleX = obj.originalSize.width / obj.size.width;
        const scaleY = obj.originalSize.height / obj.size.height;
        const localX = (point.x - obj.position.x) * scaleX;
        const localY = (point.y - obj.position.y) * scaleY;

        const layers = [...obj.layers].reverse();
        for (const layer of layers) {
            if (!layer.id) continue;
            const selector = `#${this.escapeSvgId(layer.id)}`;
            const element = container.querySelector(selector) as SVGGraphicsElement | null;
            if (!element) continue;

            const bbox = element.getBBox();
            if (
                localX >= bbox.x &&
                localX <= bbox.x + bbox.width &&
                localY >= bbox.y &&
                localY <= bbox.y + bbox.height
            ) {
                return layer.id;
            }
        }

        return null;
    }

    private parseSVGContent(svgText: string): { svgContent: string; size: Size; layers: any[] } {
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
        const svgElement = svgDoc.querySelector('svg');

        let width = 100;
        let height = 100;

        if (svgElement) {
            const wAttr = svgElement.getAttribute('width');
            const hAttr = svgElement.getAttribute('height');
            if (wAttr && hAttr) {
                width = parseFloat(wAttr);
                height = parseFloat(hAttr);
            } else {
                const viewBox = svgElement.getAttribute('viewBox');
                if (viewBox) {
                    const parts = viewBox.split(/\s+/).map(parseFloat).filter(n => !isNaN(n));
                    if (parts.length === 4) {
                        width = parts[2];
                        height = parts[3];
                    }
                }
            }
        }

        // Extract SVG layers
        const layers = this.extractSVGLayers(svgElement);

        return {
            svgContent: svgElement ? svgElement.outerHTML : svgText,
            size: { width, height },
            layers
        };
    }

    private extractSVGLayers(svgElement: SVGSVGElement | null): any[] {
        if (!svgElement) return [];

        const layers: any[] = [];
        let layerIndex = 0;

        // Find all paths, circles, rects, polygons, etc. that have fill or stroke
        const elements = svgElement.querySelectorAll('path, circle, rect, ellipse, polygon, polyline, line');

        elements.forEach((el) => {
            const fill = el.getAttribute('fill');
            const stroke = el.getAttribute('stroke');
            const id = el.getAttribute('id') || `layer-${layerIndex++}`;

            // Skip elements with no fill or stroke
            if (!fill && !stroke) return;

            // Add unique id to element if it doesn't have one
            if (!el.getAttribute('id')) {
                el.setAttribute('id', id);
            }

            layers.push({
                id: el.getAttribute('id'),
                name: el.getAttribute('id') || el.tagName,
                fill: fill || undefined,
                stroke: stroke || undefined,
                originalFill: fill || undefined,
                originalStroke: stroke || undefined
            });
        });

        return layers;
    }

    private simpleHash(str: string): number {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32bit integer
        }
        return Math.abs(hash);
    }

    private normalizeSvgForRender(svgText: string, size: Size): string {
        try {
            const parser = new DOMParser();
            const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
            const svgElement = svgDoc.querySelector('svg');
            if (!svgElement) return svgText;

            if (!svgElement.getAttribute('xmlns')) {
                svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
            }
            if (!svgElement.getAttribute('xmlns:xlink')) {
                svgElement.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
            }

            const targetWidth = String(Math.max(1, Math.round(size.width)));
            const targetHeight = String(Math.max(1, Math.round(size.height)));

            if (svgElement.getAttribute('width') !== targetWidth) {
                svgElement.setAttribute('width', targetWidth);
            }
            if (svgElement.getAttribute('height') !== targetHeight) {
                svgElement.setAttribute('height', targetHeight);
            }

            if (!svgElement.getAttribute('viewBox')) {
                svgElement.setAttribute('viewBox', `0 0 ${targetWidth} ${targetHeight}`);
            }
            if (!svgElement.getAttribute('preserveAspectRatio')) {
                svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
            }

            this.stripSvgEffectsForCanvas(svgElement);

            return svgElement.outerHTML;
        } catch {
            return svgText;
        }
    }

    private loadSvgImage(svgMarkup: string, key: string, contentHash: number): void {
        const blob = new Blob([svgMarkup], { type: 'image/svg+xml' });
        this.loadSvgImageElementFromBlob(blob, svgMarkup, key, contentHash);
    }

    private loadSvgImageElementFromBlob(blob: Blob, svgMarkup: string, key: string, contentHash: number): void {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'sync';
        (img as any)._svgHash = contentHash;
        (img as any)._svgLoading = true;
        this.imageCache.set(key, img);

        const url = URL.createObjectURL(blob);
        img.onload = () => {
            (img as any)._svgLoading = false;
            URL.revokeObjectURL(url);
            this.render();
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            this.loadSvgDataUrl(svgMarkup, key, contentHash);
        };

        img.src = url;
    }

    private loadSvgDataUrl(svgMarkup: string, key: string, contentHash: number): void {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'sync';
        (img as any)._svgHash = contentHash;
        (img as any)._svgLoading = true;
        this.imageCache.set(key, img);

        img.onload = () => {
            (img as any)._svgLoading = false;
            this.render();
        };

        img.onerror = (e) => {
            console.error('SVG image load failed', e);
        };

        const encoded = btoa(unescape(encodeURIComponent(svgMarkup)));
        img.src = `data:image/svg+xml;base64,${encoded}`;
    }

    private stripSvgEffectsForCanvas(svgElement: SVGSVGElement): void {
        const elementsWithStyle = svgElement.querySelectorAll('[style]');
        elementsWithStyle.forEach(el => {
            const style = el.getAttribute('style');
            if (!style || !style.includes('mix-blend-mode')) return;
            const nextStyle = style
                .split(';')
                .map(part => part.trim())
                .filter(part => part && !part.startsWith('mix-blend-mode'))
                .join('; ');
            if (nextStyle) {
                el.setAttribute('style', nextStyle);
            } else {
                el.removeAttribute('style');
            }
        });

        const defsToRemove = svgElement.querySelectorAll('filter, mask, clipPath');
        defsToRemove.forEach(el => el.parentElement?.removeChild(el));

        const attributesToClear = ['filter', 'mask', 'clip-path'];
        attributesToClear.forEach(attr => {
            svgElement.querySelectorAll(`[${attr}]`).forEach(el => el.removeAttribute(attr));
        });
    }
}
